import React, { useState, useEffect } from 'react';
import {
    Search,
    Filter,
    Download,
    MoreVertical,
    CheckCircle,
    XCircle,
    Clock,
    User,
    Mail,
    Phone,
    CreditCard,
    Trash2
} from 'lucide-react';
import { PageHeader } from "@/components/layout/page-header";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import * as api from "@/lib/api/endpoints";
import { toast } from 'react-hot-toast';
import { PermissionGuard } from '@/components/guards/PermissionGuard';
import { Member } from "@/lib/api/types";

export default function MembersPage() {
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showIncomplete, setShowIncomplete] = useState(false);

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            const data = await api.members.list();
            setMembers(data);
        } catch (error) {
            toast.error('Failed to load members');
        } finally {
            setLoading(false);
        }
    };

    const handleMerchToggle = async (memberId: string, currentStatus: boolean | undefined) => {
        try {
            // Optimistic update
            setMembers(members.map(m =>
                m.id === memberId ? { ...m, merchDistributed: !currentStatus } : m
            ));

            await api.members.updateMerchStatus(memberId, !currentStatus);

            toast.success('Merch status updated');
        } catch (error) {
            setMembers(members.map(m =>
                m.id === memberId ? { ...m, merchDistributed: !(!currentStatus) } : m // Revert
            ));
            toast.error('Error updating merch status');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this member? This action cannot be undone.')) return;

        try {
            await api.members.delete(id);
            setMembers(members.filter(m => m.id !== id));
            toast.success('Member deleted successfully');
        } catch (error) {
            toast.error('Failed to delete member');
        }
    };

    const filteredMembers = members.filter(member => {
        const matchesSearch =
            member.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (member.memberId && member.memberId.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStatus = statusFilter === 'all' || member.status === statusFilter;

        // Filter out incomplete registrations (pending status AND no/zero amount/null amount) unless showIncomplete is true
        // "Unpaid" usually means amountPaid is 0 or null.
        // Pending usually means payment not successful.
        const isComplete = member.status === 'active' || (member.amountPaid && member.amountPaid > 0);
        const matchesCompletion = showIncomplete || isComplete;

        return matchesSearch && matchesStatus && matchesCompletion;
    });

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex-1">
                    <PageHeader
                        title="Members"
                    />
                </div>
                <button
                    onClick={() => {
                        // Generate CSV content
                        const headers = ['Full Name', 'Member ID', 'Email', 'Phone', 'Status', 'Tier', 'Amount Paid (KES)', 'Merch Status', 'Joined Date'];
                        const csvRows = [
                            headers.join(','),
                            ...filteredMembers.map(member => [
                                `"${member.fullName}"`,
                                member.memberId || 'N/A',
                                member.email,
                                member.phoneNumber,
                                member.status,
                                member.tier || 'basic',
                                member.amountPaid ? (member.amountPaid).toFixed(2) : '0.00',
                                member.merchDistributed ? 'Distributed' : 'Pending',
                                new Date(member.createdAt).toLocaleDateString()
                            ].join(','))
                        ];

                        // Create and download CSV file
                        const csvContent = csvRows.join('\n');
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                        const link = document.createElement('a');
                        const url = URL.createObjectURL(blob);
                        link.setAttribute('href', url);
                        link.setAttribute('download', `members_export_${new Date().toISOString().split('T')[0]}.csv`);
                        link.style.visibility = 'hidden';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        toast.success('Members exported successfully!');
                    }}
                    className="bg-rose-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-rose-700 transition-colors"
                >
                    <Download size={20} />
                    Export CSV
                </button>
            </div>

            {/* View Options */}
            <div className="flex justify-end items-center gap-2">
                <Switch
                    id="show-incomplete"
                    checked={showIncomplete}
                    onCheckedChange={setShowIncomplete}
                />
                <Label htmlFor="show-incomplete" className="text-sm text-muted-foreground cursor-pointer">
                    Show Pending/Incomplete Registrations
                </Label>
            </div>

            {/* Filters */}
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 bg-card p-4 rounded-xl shadow-sm border border-border">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Search members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                    />
                </div>
                <div className="flex items-center gap-2 border-l pl-4">
                    <Filter size={20} className="text-gray-400" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="border-none bg-transparent focus:ring-0 text-gray-600 font-medium"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-card rounded-xl shadow-sm border border-border overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                        <tr>
                            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Member</th>
                            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Paid</th>
                            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Merch</th>
                            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined Date</th>
                            <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="py-8 text-center text-gray-500">Loading members...</td>
                            </tr>
                        ) : filteredMembers.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="py-8 text-center text-gray-500">No members found</td>
                            </tr>
                        ) : (
                            filteredMembers.map((member) => (
                                <tr key={member.id} className="hover:bg-muted/50 transition-colors">
                                    <td className="py-4 px-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 font-bold">
                                                {member.fullName.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-medium text-foreground">{member.fullName}</p>
                                                <p className="text-xs text-muted-foreground font-mono">{member.memberId || 'No ID'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-4 px-6">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Mail size={14} />
                                                {member.email}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <Phone size={14} />
                                                {member.phoneNumber}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-4 px-6">
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${member.status === 'active'
                                            ? 'bg-green-50 text-green-700 border border-green-100'
                                            : 'bg-yellow-50 text-yellow-700 border border-yellow-100'
                                            }`}>
                                            {member.status === 'active' ? <CheckCircle size={12} /> : <Clock size={12} />}
                                            {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                                        </span>
                                    </td>
                                    <td className="py-4 px-6 text-sm text-foreground font-medium">
                                        {member.amountPaid ? (
                                            new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(member.amountPaid)
                                        ) : (
                                            <span className="text-muted-foreground">-</span>
                                        )}
                                        {member.tier === 'premium' && (
                                            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                                                Premium
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-4 px-6">
                                        <PermissionGuard resource="members" action="update">
                                            {member.tier === 'premium' ? (
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={!!member.merchDistributed}
                                                        onCheckedChange={() => handleMerchToggle(member.id, member.merchDistributed)}
                                                    />
                                                    <span className="text-xs text-gray-500">
                                                        {member.merchDistributed ? 'Distributed' : 'Pending'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">
                                                    Not Eligible
                                                </span>
                                            )}
                                        </PermissionGuard>
                                    </td>
                                    <td className="py-4 px-6 text-sm text-gray-600">
                                        {new Date(member.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="py-4 px-6 text-right">
                                        <PermissionGuard resource="members" action="read">
                                            <div className="relative group">
                                                <button className="text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-muted/50">
                                                    <MoreVertical size={18} />
                                                </button>
                                                <div className="absolute right-0 mt-1 w-48 bg-popover rounded-lg shadow-lg border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                                    <div className="py-1">
                                                        <a
                                                            href={`https://elimishacharity.org/team/members/${member.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                        >
                                                            <User size={14} />
                                                            View Profile
                                                        </a>
                                                        <a
                                                            href={`https://elimishacharity.org/membership/card/${member.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                        >
                                                            <CreditCard size={14} />
                                                            View Badge
                                                        </a>
                                                        <button
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(`https://elimishacharity.org/team/members/${member.id}`);
                                                                toast.success('Profile link copied!');
                                                            }}
                                                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                                                        >
                                                            <Download size={14} />
                                                            Copy Profile Link
                                                        </button>
                                                    </div>

                                                    <PermissionGuard resource="members" action="delete">
                                                        <div className="border-t border-border mt-1 pt-1 mb-1">
                                                            <button
                                                                onClick={() => handleDelete(member.id)}
                                                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                                                            >
                                                                <Trash2 size={14} />
                                                                Delete Member
                                                            </button>
                                                        </div>
                                                    </PermissionGuard>
                                                </div>
                                            </div>
                                        </PermissionGuard>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
